import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMatch, useNavigate, useSearchParams } from 'react-router-dom';

import { storeSearch } from 'store/modules/routerUtils/actions';
import { useStoredSearch } from 'store/modules/routerUtils/hooks';

export const useApplyCachedSearchParams = (path: string) => {
  const [applied, setApplied] = useState(false);
  const [searchParams] = useSearchParams();
  const cachedSearchString = useStoredSearch(path);
  const currentSearchString = searchParams.toString();
  const dispatch = useDispatch();
  const isPageMatchedExact = !!useMatch({ path, end: true });
  const navigate = useNavigate();

  // *************** On mount, retrieve and apply search params from cache ***************
  useEffect(() => {
    if (isPageMatchedExact) {
      // Respect the current search params if they are not empty.
      if (currentSearchString === '' && cachedSearchString !== '') {
        navigate({ search: cachedSearchString }, { replace: true });
      }
      setApplied(true);
    } else {
      setApplied(false);
    }
    // The storedSearches might change, but we only want to apply the cached search params once per path change.
    // Therefore, we subscribe to 'isPageMatchedExact' but not 'cachedSearchString'.
    // The 'isPageMatchedExact' triggers re-runs when the user navigates between List and (OverlayPage) Details pages.
    // We also subscribe to 'path' in case the user navigates among assetType:/:assetTypeId pages, which all
    // return true for 'isPageMatchedExact', making the effect not re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageMatchedExact, path]);

  // *************** On unmount, store search params in cache for later use ***************
  useEffect(() => {
    return () => {
      if (isPageMatchedExact) {
        dispatch(storeSearch(path, currentSearchString));
      }
    };
  }, [currentSearchString, dispatch, isPageMatchedExact, path]);

  return {
    // Ideally, consumers of the util should not care about this.
    // But SearchInput in SamplePointsTable is particularly sensitive.
    /**
     * Indicates if cached search params have been applied.
     * Useful for components sensitive to the order of search params being applied between navigations.
     */
    applied
  };
};
